import React, { useState, useEffect } from 'react';
import eventosService from '../services/eventosService';
import eventosGanttService from '../services/eventosGanttService';
import {
  clienteService,
  proyectoService,
  presupuestoCostoService
} from '../services/onebaseService';
import contentService from '../services/contentService';
import CommentsSection from './CommentsSection';
import GanttChart from './GanttChart';
import TareaEventoModal from './TareaEventoModal';
import TareaEventoTimeModal from './TareaEventoTimeModal';
import SolicitudRecursosModal from './SolicitudRecursosModal';
import Loading from './Loading';
import './PostDetailModal.css';

const EventoDetailModal = ({ eventoId, onClose, onUpdate }) => {
  const [eventoData, setEventoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');
  const [editModeInfo, setEditModeInfo] = useState(false);
  const [infoFormData, setInfoFormData] = useState({});
  const [users, setUsers] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [proyectosFiltrados, setProyectosFiltrados] = useState([]);
  const [presupuestosCosto, setPresupuestosCosto] = useState([]);
  const [presupuestosCostoFiltrados, setPresupuestosCostoFiltrados] = useState([]);
  const [desarrollaList, setDesarrollaList] = useState([]);
  const [tareasDelEvento, setTareasDelEvento] = useState([]);
  const [loadingTareas, setLoadingTareas] = useState(false);
  const [showTareaModal, setShowTareaModal] = useState(false);
  const [actividadSeleccionadaParaTarea, setActividadSeleccionadaParaTarea] = useState(null);
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [selectedTareaForTime, setSelectedTareaForTime] = useState(null);
  const [showManualTimeForm, setShowManualTimeForm] = useState(false);
  const [manualTimeForm, setManualTimeForm] = useState({
    fecha: new Date().toISOString().split('T')[0],
    minutos: '',
    notas: '',
    presupuesto_costo_recursos: []
  });
  const [recursosDisponiblesTarea, setRecursosDisponiblesTarea] = useState([]);
  const [showSolicitudRecursosModal, setShowSolicitudRecursosModal] = useState(false);
  const [tareaSeleccionadaParaSolicitud, setTareaSeleccionadaParaSolicitud] = useState(null);

  useEffect(() => {
    if (eventoId) {
      loadEventoDetail();
      loadUsers();
      loadClientes();
      loadProyectos();
      loadPresupuestosCosto();
      loadDesarrolla();
      loadTareasDelEvento();
    }
  }, [eventoId]);

  // Filtrar proyectos según el cliente seleccionado
  useEffect(() => {
    const clienteId = parseInt(infoFormData.cliente);
    const matchCliente = (p) => {
      if (!p.cliente) return false;
      if (typeof p.cliente === 'object') return p.cliente.id === clienteId;
      return parseInt(p.cliente) === clienteId;
    };
    if (infoFormData.cliente) {
      setProyectosFiltrados(proyectos.filter(matchCliente));
    } else {
      setProyectosFiltrados(proyectos);
    }
  }, [infoFormData.cliente, proyectos]);

  // Filtrar presupuestos según el proyecto seleccionado
  useEffect(() => {
    if (infoFormData.proyecto) {
      const presupuestosPorProyecto = presupuestosCosto.filter(
        p => p.proyecto === parseInt(infoFormData.proyecto)
      );
      setPresupuestosCostoFiltrados(presupuestosPorProyecto);
    } else {
      setPresupuestosCostoFiltrados(presupuestosCosto);
    }
  }, [infoFormData.proyecto, presupuestosCosto]);

  // Escuchar eventos de actualización de tareas y timelogs
  useEffect(() => {
    const handleTareaActualizada = () => {
      console.log('📢 Evento tareaActualizada recibido - Recargando tareas...');
      loadTareasDelEvento();
    };

    const handleTimeLogAdded = () => {
      console.log('📢 Evento tareaTimeLogAdded recibido - Recargando tareas...');
      loadTareasDelEvento();
    };

    // Agregar listeners
    window.addEventListener('tareaActualizada', handleTareaActualizada);
    window.addEventListener('tareaTimeLogAdded', handleTimeLogAdded);

    // Cleanup: remover listeners cuando el componente se desmonte
    return () => {
      window.removeEventListener('tareaActualizada', handleTareaActualizada);
      window.removeEventListener('tareaTimeLogAdded', handleTimeLogAdded);
    };
  }, [eventoId]); // Dependencia en eventoId para que loadTareasDelEvento tenga el eventoId correcto

  // Recargar tareas cuando se cambia a la pestaña "Tareas"
  useEffect(() => {
    if (activeTab === 'tasks' && eventoId) {
      console.log('🔄 Cambiando a pestaña Tareas - Recargando datos...');
      loadTareasDelEvento();
    }
  }, [activeTab, eventoId]);

  const loadEventoDetail = async () => {
    try {
      setLoading(true);
      const response = await eventosService.getEvento(eventoId);
      console.log('📅 Evento detail:', response.data);
      setEventoData(response.data);
      setInfoFormData({
        title: response.data.title || '',
        description: response.data.description || '',
        status: response.data.status || 'draft',
        tipo_evento: response.data.tipo_evento || 'otro',
        cliente: response.data.cliente || '',
        proyecto: response.data.proyecto || '',
        presupuesto_costo: response.data.presupuesto_costo || '',
        desarrolla: response.data.desarrolla || '',
        fecha_inicio: response.data.fecha_inicio || '',
        fecha_termino: response.data.fecha_termino || '',
        hora_inicio: response.data.hora_inicio || '',
        hora_fin: response.data.hora_fin || '',
        ubicacion: response.data.ubicacion || '',
        capacidad_maxima: response.data.capacidad_maxima || '',
        notas: response.data.notas || ''
      });
    } catch (error) {
      console.error('Error loading evento:', error);
      alert('Error al cargar el evento');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await contentService.getUsers();
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadClientes = async () => {
    try {
      const response = await clienteService.getAll();
      const clientesData = response.data.results || response.data || [];
      setClientes(Array.isArray(clientesData) ? clientesData : []);
    } catch (error) {
      console.error('Error loading clientes:', error);
      setClientes([]);
    }
  };

  const loadProyectos = async () => {
    try {
      const response = await proyectoService.getAll();
      const proyectosData = response.data.results || response.data || [];
      setProyectos(Array.isArray(proyectosData) ? proyectosData : []);
    } catch (error) {
      console.error('Error loading proyectos:', error);
      setProyectos([]);
    }
  };

  const loadPresupuestosCosto = async () => {
    try {
      const response = await presupuestoCostoService.getAll();
      const presupuestosData = response.data.results || response.data || [];
      setPresupuestosCosto(Array.isArray(presupuestosData) ? presupuestosData : []);
    } catch (error) {
      console.error('Error loading presupuestos:', error);
      setPresupuestosCosto([]);
    }
  };

  const loadDesarrolla = async () => {
    try {
      const response = await contentService.getDesarrolla();
      const desarrollaData = response.data.results || response.data || [];
      setDesarrollaList(Array.isArray(desarrollaData) ? desarrollaData : []);
    } catch (error) {
      console.error('Error loading desarrolla:', error);
      setDesarrollaList([]);
    }
  };

  const loadTareasDelEvento = async () => {
    try {
      setLoadingTareas(true);
      const response = await eventosGanttService.tareas.listByEvento(eventoId);
      const tareasData = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setTareasDelEvento(tareasData);
    } catch (error) {
      console.error('Error loading tareas:', error);
      setTareasDelEvento([]);
    } finally {
      setLoadingTareas(false);
    }
  };

  const handleInfoEditToggle = () => {
    if (editModeInfo) {
      // Cancelar: restaurar datos originales
      setInfoFormData({
        title: eventoData.title || '',
        description: eventoData.description || '',
        status: eventoData.status || 'draft',
        tipo_evento: eventoData.tipo_evento || 'otro',
        cliente: eventoData.cliente || '',
        proyecto: eventoData.proyecto || '',
        presupuesto_costo: eventoData.presupuesto_costo || '',
        desarrolla: eventoData.desarrolla || '',
        fecha_inicio: eventoData.fecha_inicio || '',
        fecha_termino: eventoData.fecha_termino || '',
        hora_inicio: eventoData.hora_inicio || '',
        hora_fin: eventoData.hora_fin || '',
        ubicacion: eventoData.ubicacion || '',
        capacidad_maxima: eventoData.capacidad_maxima || '',
        notas: eventoData.notas || '',
        tipo_material: eventoData.tipo_material || '',
        influencer: eventoData.influencer || '',
      });
    }
    setEditModeInfo(!editModeInfo);
  };

  const handleInfoInputChange = (field, value) => {
    setInfoFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveInfo = async () => {
    try {
      const submitData = {
        title: infoFormData.title,
        description: infoFormData.description,
        status: infoFormData.status,
        tipo_evento: infoFormData.tipo_evento,
        desarrolla: infoFormData.desarrolla || null,
        tipo_material: infoFormData.tipo_material,
        influencer: infoFormData.influencer,
      };

      await eventosService.updateEvento(eventoId, submitData);
      alert('✅ Evento actualizado correctamente');
      setEditModeInfo(false);
      loadEventoDetail();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating evento:', error);
      alert('❌ Error al actualizar el evento');
    }
  };

  const handleDeleteEvento = async () => {
    // Confirmar eliminación
    if (!window.confirm('¿Estás seguro de que deseas eliminar este evento? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      console.log('🗑️ Eliminando evento con ID:', eventoId);
      await eventosService.deleteEvento(eventoId);
      alert('✅ Evento eliminado exitosamente');

      // Cerrar modal y notificar actualización
      if (onUpdate) onUpdate();
      onClose();

      // Recargar la página después de un pequeño delay
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      console.error('Error al eliminar evento:', error);
      alert(error.response?.data?.error || 'Error al eliminar el evento');
    }
  };

  const handleDeleteTareaEvento = async (tareaId) => {
    // Confirmar eliminación
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta tarea? Esta acción también eliminará la tarea de Kanban asociada si existe.')) {
      return;
    }

    try {
      console.log('🗑️ Eliminando TareaEvento con ID:', tareaId);
      await eventosService.deleteTareaEvento(tareaId);
      alert('✅ Tarea eliminada exitosamente');

      // Recargar las tareas del evento
      loadTareasDelEvento();
    } catch (error) {
      console.error('Error al eliminar tarea:', error);
      alert(error.response?.data?.error || 'Error al eliminar la tarea');
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      draft: 'status-draft',
      planning: 'status-planning',
      confirmed: 'status-confirmed',
      in_progress: 'status-in-progress',
      completed: 'status-completed',
      cancelled: 'status-cancelled',
      postponed: 'status-postponed'
    };
    return statusClasses[status] || 'status-default';
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      draft: 'Borrador',
      planning: 'En Planificación',
      confirmed: 'Confirmado',
      in_progress: 'En Progreso',
      completed: 'Completado',
      cancelled: 'Cancelado',
      postponed: 'Pospuesto'
    };
    return statusLabels[status] || status;
  };

  // Cargar recursos disponibles de la tarea seleccionada
  const cargarRecursosDeTarea = async (tarea) => {
    if (!tarea || !tarea.id) {
      console.log('❌ No hay tarea o tarea.id');
      setRecursosDisponiblesTarea([]);
      return;
    }

    try {
      console.log('🔍 Cargando recursos para tarea:', tarea.id);

      // Obtener el detalle de la tarea para acceder a sus recursos
      const response = await eventosGanttService.tareas.retrieve(tarea.id);
      const tareaDetalle = response.data;
      console.log('📋 Tarea detalle:', tareaDetalle);
      console.log('📦 Recursos detalle:', tareaDetalle.presupuesto_costo_recursos_detalle);

      // Si la tarea tiene recursos asociados, filtrarlos
      if (tareaDetalle.presupuesto_costo_recursos_detalle && tareaDetalle.presupuesto_costo_recursos_detalle.length > 0) {
        // Filtrar solo los recursos que sean de tipo "Mano de obra"
        const recursosManoDeObra = tareaDetalle.presupuesto_costo_recursos_detalle.filter(r => {
          const tipoRecurso = r.recurso_referencia_data?.tipo_recurso?.nombre;
          const esManoDeObra = tipoRecurso === 'Mano de obra';

          console.log(`   Recurso "${r.nombre}":`, {
            id: r.id,
            tipoRecurso,
            esManoDeObra,
            recurso_referencia_data: r.recurso_referencia_data
          });

          return esManoDeObra;
        });

        console.log('✅ Recursos filtrados (Mano de obra):', recursosManoDeObra);
        setRecursosDisponiblesTarea(recursosManoDeObra);
      } else {
        console.log('⚠️ La tarea no tiene recursos asociados');
        setRecursosDisponiblesTarea([]);
      }
    } catch (error) {
      console.error('❌ Error al cargar recursos de la tarea:', error);
      setRecursosDisponiblesTarea([]);
    }
  };

  // Manejar agregar tiempo manual
  const handleAddManualTime = async (e) => {
    e.preventDefault();

    if (!manualTimeForm.fecha.trim()) {
      alert('La fecha es requerida');
      return;
    }

    if (!manualTimeForm.minutos || parseFloat(manualTimeForm.minutos) <= 0) {
      alert('Por favor ingresa un tiempo válido en minutos (mayor a 0)');
      return;
    }

    try {
      const timelogData = {
        tarea: selectedTareaForTime.id,
        fecha: manualTimeForm.fecha,
        minutos: parseInt(manualTimeForm.minutos),
        notas: manualTimeForm.notas,
        presupuesto_costo_recursos: manualTimeForm.presupuesto_costo_recursos
      };

      console.log('📤 Enviando TimeLog con datos:', timelogData);
      console.log('   🎯 Recursos en manualTimeForm.presupuesto_costo_recursos:', manualTimeForm.presupuesto_costo_recursos);
      console.log('   📦 Recursos en timelogData.presupuesto_costo_recursos:', timelogData.presupuesto_costo_recursos);
      console.log('   📋 Recursos disponibles en la tarea:', recursosDisponiblesTarea.map(r => r.id));

      await eventosGanttService.timelogs.create(timelogData);
      console.log('✅ TimeLog creado exitosamente');

      // Recalcular horas de la tarea
      await eventosGanttService.tareas.recalcularHoras(selectedTareaForTime.id);
      console.log('✅ Horas recalculadas para la tarea:', selectedTareaForTime.id);

      // Pequeño delay para asegurar que el backend procese la actualización
      await new Promise(resolve => setTimeout(resolve, 300));

      // Recargar tareas para actualizar el badge de tiempo total
      await loadTareasDelEvento();
      console.log('✅ Tareas recargadas - Badge actualizado');

      alert('✅ Tiempo registrado correctamente');

      // Cerrar formulario y limpiar
      setShowManualTimeForm(false);
      setSelectedTareaForTime(null);
      setRecursosDisponiblesTarea([]);
      setManualTimeForm({
        fecha: new Date().toISOString().split('T')[0],
        minutos: '',
        notas: '',
        presupuesto_costo_recursos: []
      });

      // Disparar evento global para notificar cambios
      window.dispatchEvent(new CustomEvent('tareaTimeLogAdded', { detail: { tareaId: selectedTareaForTime.id } }));
    } catch (error) {
      console.error('Error al agregar tiempo:', error);
      const errorMsg = error.response?.data?.detail ||
                      Object.values(error.response?.data || {}).flat().join(', ') ||
                      'Error al agregar tiempo';
      alert(errorMsg);
    }
  };

  const getTipoEventoLabel = (tipo) => {
    const tipoLabels = {
      lanzamiento: 'Lanzamiento de Producto',
      corporativo: 'Evento Corporativo',
      social: 'Evento Social',
      conferencia: 'Conferencia',
      taller: 'Taller/Workshop',
      webinar: 'Webinar',
      networking: 'Networking',
      otro: 'Otro'
    };
    return tipoLabels[tipo] || tipo;
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content modal-xl" onClick={(e) => e.stopPropagation()}>
          <Loading fullScreen={false} />
        </div>
      </div>
    );
  }

  if (!eventoData) {
    return null;
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-xl detail-modal" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="detail-header">
            <div className="detail-header-left">
              <h2>🎯 {eventoData.title}</h2>
              <span className={`status-badge ${getStatusBadgeClass(eventoData.status)}`}>
                {getStatusLabel(eventoData.status)}
              </span>
            </div>
            <div className="detail-header-actions">
              <button onClick={onClose} className="close-btn">×</button>
            </div>
          </div>

          {/* Tabs */}
          <div className="detail-tabs">
            <button
              className={`tab-btn ${activeTab === 'general' ? 'active' : ''}`}
              onClick={() => setActiveTab('general')}
            >
              📄 General
            </button>
            <button
              className={`tab-btn ${activeTab === 'tasks' ? 'active' : ''}`}
              onClick={() => setActiveTab('tasks')}
            >
              ✓ Tareas
            </button>
            <button
              className={`tab-btn ${activeTab === 'gantt' ? 'active' : ''}`}
              onClick={() => setActiveTab('gantt')}
            >
              📊 Gantt
            </button>
            <button
              className={`tab-btn ${activeTab === 'comments' ? 'active' : ''}`}
              onClick={() => setActiveTab('comments')}
            >
              💬 Comentarios ({eventoData.comments?.length || 0})
            </button>
          </div>

          {/* Content */}
          <div className="detail-content">
            {activeTab === 'general' && (
              <div className="info-section">
                {/* Botón de edición */}
                <div className="section-header">
                  <h3>📄 Información del Evento</h3>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={handleInfoEditToggle}
                      className="btn-edit-info"
                    >
                      {editModeInfo ? '❌ Cancelar' : '✏️ Editar Información'}
                    </button>
                    <button
                      onClick={handleDeleteEvento}
                      className="btn-delete-post"
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#dc2626'}
                      onMouseLeave={(e) => e.target.style.background = '#ef4444'}
                    >
                      🗑️ Eliminar Evento
                    </button>
                  </div>
                </div>

                {editModeInfo ? (
                  // Modo edición
                  <div className="edit-info-form">
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Título del Evento:</label>
                        <input
                          type="text"
                          value={infoFormData.title}
                          onChange={(e) => handleInfoInputChange('title', e.target.value)}
                          placeholder="Título del evento"
                        />
                      </div>

                      <div className="form-group">
                        <label>Tipo de Evento:</label>
                        <select
                          value={infoFormData.tipo_evento}
                          onChange={(e) => handleInfoInputChange('tipo_evento', e.target.value)}
                        >
                          <option value="lanzamiento">Lanzamiento de Producto</option>
                          <option value="corporativo">Evento Corporativo</option>
                          <option value="social">Evento Social</option>
                          <option value="conferencia">Conferencia</option>
                          <option value="taller">Taller/Workshop</option>
                          <option value="webinar">Webinar</option>
                          <option value="networking">Networking</option>
                          <option value="otro">Otro</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Estado:</label>
                        <select
                          value={infoFormData.status}
                          onChange={(e) => handleInfoInputChange('status', e.target.value)}
                        >
                          <option value="draft">Borrador</option>
                          <option value="planning">En Planificación</option>
                          <option value="confirmed">Confirmado</option>
                          <option value="in_progress">En Progreso</option>
                          <option value="completed">Completado</option>
                          <option value="cancelled">Cancelado</option>
                          <option value="postponed">Pospuesto</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Cliente:</label>
                        <select
                          value={infoFormData.cliente}
                          onChange={(e) => handleInfoInputChange('cliente', e.target.value)}
                        >
                          <option value="">Seleccionar cliente</option>
                          {clientes.map(cliente => (
                            <option key={cliente.id} value={cliente.id}>
                              {cliente.nombre}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Proyecto:</label>
                        <select
                          value={infoFormData.proyecto}
                          onChange={(e) => handleInfoInputChange('proyecto', e.target.value)}
                          disabled={!infoFormData.cliente}
                        >
                          <option value="">
                            {infoFormData.cliente ? 'Seleccionar proyecto' : 'Primero selecciona un cliente'}
                          </option>
                          {proyectosFiltrados.map(proyecto => (
                            <option key={proyecto.id} value={proyecto.id}>
                              {proyecto.nombre}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Presupuesto de Costo:</label>
                        <select
                          value={infoFormData.presupuesto_costo}
                          onChange={(e) => handleInfoInputChange('presupuesto_costo', e.target.value)}
                          disabled={!infoFormData.proyecto}
                        >
                          <option value="">
                            {infoFormData.proyecto ? 'Seleccionar presupuesto' : 'Primero selecciona un proyecto'}
                          </option>
                          {presupuestosCostoFiltrados.map(presupuesto => (
                            <option key={presupuesto.id} value={presupuesto.id}>
                              {presupuesto.nombre || `Presupuesto #${presupuesto.id}`}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Desarrolla:</label>
                        <select
                          value={infoFormData.desarrolla}
                          onChange={(e) => handleInfoInputChange('desarrolla', e.target.value)}
                        >
                          <option value="">Seleccionar</option>
                          {desarrollaList.map(desarrolla => (
                            <option key={desarrolla.id} value={desarrolla.id}>
                              {desarrolla.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Fecha de Inicio:</label>
                        <input
                          type="date"
                          value={infoFormData.fecha_inicio}
                          onChange={(e) => handleInfoInputChange('fecha_inicio', e.target.value)}
                        />
                      </div>

                      <div className="form-group">
                        <label>Fecha de Término:</label>
                        <input
                          type="date"
                          value={infoFormData.fecha_termino}
                          onChange={(e) => handleInfoInputChange('fecha_termino', e.target.value)}
                        />
                      </div>

                      <div className="form-group">
                        <label>Hora de Inicio:</label>
                        <input
                          type="time"
                          value={infoFormData.hora_inicio}
                          onChange={(e) => handleInfoInputChange('hora_inicio', e.target.value)}
                        />
                      </div>

                      <div className="form-group">
                        <label>Hora de Fin:</label>
                        <input
                          type="time"
                          value={infoFormData.hora_fin}
                          onChange={(e) => handleInfoInputChange('hora_fin', e.target.value)}
                        />
                      </div>

                      <div className="form-group full-width">
                        <label>Ubicación:</label>
                        <input
                          type="text"
                          value={infoFormData.ubicacion}
                          onChange={(e) => handleInfoInputChange('ubicacion', e.target.value)}
                          placeholder="Dirección física o enlace virtual"
                        />
                      </div>

                      <div className="form-group">
                        <label>Capacidad Máxima:</label>
                        <input
                          type="number"
                          value={infoFormData.capacidad_maxima}
                          onChange={(e) => handleInfoInputChange('capacidad_maxima', e.target.value)}
                          min="0"
                          placeholder="Número de asistentes"
                        />
                      </div>

                      <div className="form-group full-width">
                        <label>Descripción:</label>
                        <textarea
                          value={infoFormData.description}
                          onChange={(e) => handleInfoInputChange('description', e.target.value)}
                          rows="3"
                          placeholder="Descripción del evento"
                        />
                      </div>

                      <div className="form-group full-width">
                        <label>Notas y Observaciones:</label>
                        <textarea
                          value={infoFormData.notas}
                          onChange={(e) => handleInfoInputChange('notas', e.target.value)}
                          rows="4"
                          placeholder="Notas adicionales sobre el evento"
                        />
                      </div>
                    </div>

                    <div className="form-actions">
                      <button onClick={handleInfoEditToggle} className="btn-secondary">
                        Cancelar
                      </button>
                      <button onClick={handleSaveInfo} className="btn-primary">
                        💾 Guardar Cambios
                      </button>
                    </div>
                  </div>
                ) : (
                  // Modo vista
                  <div className="info-display">
                    {/* Sección Evento */}
                    <div style={{
                      background: '#f9fafb',
                      padding: '20px',
                      borderRadius: '12px',
                      borderLeft: '4px solid #69B32D',
                      marginBottom: '20px'
                    }}>
                      <h4 style={{ marginBottom: '1rem', color: '#1f2937', fontSize: '16px', fontWeight: '700', margin: '0 0 16px 0' }}>🎭 Evento</h4>
                      <div className="info-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div className="info-item">
                          <label>Cliente:</label>
                          <span>{eventoData.cliente_nombre || 'No asignado'}</span>
                        </div>

                        <div className="info-item">
                          <label>Tipo de Evento:</label>
                          <span>{getTipoEventoLabel(eventoData.tipo_evento)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Sección Proyecto */}
                    <div style={{
                      background: '#f9fafb',
                      padding: '20px',
                      borderRadius: '12px',
                      borderLeft: '4px solid #3b82f6',
                      marginBottom: '20px'
                    }}>
                      <h4 style={{ marginBottom: '1rem', color: '#1f2937', fontSize: '16px', fontWeight: '700', margin: '0 0 16px 0' }}>📊 Proyecto</h4>
                      <div className="info-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div className="info-item">
                          <label>Proyecto:</label>
                          <span>{eventoData.proyecto_nombre || 'No asignado'}</span>
                        </div>

                        <div className="info-item">
                          <label>Presupuesto Costo:</label>
                          <span>{eventoData.presupuesto_costo_nombre || 'No asignado'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Sección Información */}
                    <div style={{
                      background: '#f9fafb',
                      padding: '20px',
                      borderRadius: '12px',
                      borderLeft: '4px solid #69B32D',
                      marginBottom: '20px'
                    }}>
                      <h4 style={{ marginBottom: '1rem', color: '#1f2937', fontSize: '16px', fontWeight: '700', margin: '0 0 16px 0' }}>ℹ️ Información</h4>
                      <div className="info-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div className="info-item">
                          <label>Fecha de Inicio:</label>
                          <span>📅 {eventoData.fecha_inicio}</span>
                        </div>

                        <div className="info-item">
                          <label>Fecha de Término:</label>
                          <span>{eventoData.fecha_termino ? `📅 ${eventoData.fecha_termino}` : 'No definida'}</span>
                        </div>

                        <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                          <label>Dirección:</label>
                          <span>{eventoData.ubicacion || 'No especificada'}</span>
                        </div>

                        <div className="info-item">
                          <label>Capacidad:</label>
                          <span>{eventoData.capacidad_maxima ? `👥 ${eventoData.capacidad_maxima}` : 'No definida'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Descripción y Notas */}
                    {(eventoData.description || eventoData.notas) && (
                      <div style={{
                        background: '#f9fafb',
                        padding: '20px',
                        borderRadius: '12px',
                        borderLeft: '4px solid #69B32D'
                      }}>
                        {eventoData.description && (
                          <div className="info-item" style={{ marginBottom: '1.5rem' }}>
                            <label>Descripción:</label>
                            <span>{eventoData.description}</span>
                          </div>
                        )}

                        {eventoData.notas && (
                          <div className="info-item">
                            <label>Notas y Observaciones:</label>
                            <span>{eventoData.notas}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'tasks' && (
              <div className="tasks-section">
                <div className="section-header">
                  <h3>📋 Tareas del Evento ({tareasDelEvento.length})</h3>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                  }}>
                    {/* Badge de tiempo total registrado */}
                    {tareasDelEvento.length > 0 && (() => {
                      const totalHoras = tareasDelEvento.reduce((sum, tarea) => sum + (parseFloat(tarea.total_horas) || 0), 0);
                      const horas = Math.floor(totalHoras);
                      const minutos = Math.round((totalHoras - horas) * 60);
                      const tiempoFormato = `${String(horas).padStart(2, '0')}:${String(minutos).padStart(2, '0')}:00`;
                      return (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.5rem 1rem',
                          background: '#dbeafe',
                          color: '#1e40af',
                          borderRadius: '9999px',
                          fontSize: '0.875rem',
                          fontWeight: '600'
                        }}>
                          <span>⏱️</span>
                          <span>Registrado: {tiempoFormato}</span>
                        </div>
                      );
                    })()}

                    <button
                      onClick={() => {
                        setActividadSeleccionadaParaTarea(null);
                        setShowTareaModal(true);
                      }}
                      style={{
                        padding: '0.75rem 1.5rem',
                        background: 'linear-gradient(135deg, #102826 0%, #69B32D 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        transition: 'all 0.2s',
                        transform: 'translateY(0px)',
                        boxShadow: 'none'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      ➕ Nueva Tarea
                    </button>
                  </div>
                </div>

                {loadingTareas ? (
                  <div style={{
                    background: '#f9fafb',
                    borderRadius: '0.5rem',
                    padding: '2rem',
                    textAlign: 'center',
                    color: '#6b7280'
                  }}>
                    <Loading fullScreen={false} size="sm" mensaje="Cargando tareas" />
                  </div>
                ) : tareasDelEvento.length === 0 ? (
                  <div style={{
                    background: '#f9fafb',
                    borderRadius: '0.5rem',
                    padding: '2rem',
                    textAlign: 'center',
                    color: '#6b7280'
                  }}>
                    <p style={{ margin: 0, fontSize: '0.875rem' }}>
                      No hay tareas asociadas a este evento
                    </p>
                  </div>
                ) : (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem'
                  }}>
                    {tareasDelEvento.map(tarea => (
                      <div
                        key={tarea.id}
                        onClick={() => {
                          // Podrías abrir un modal de edición aquí si lo deseas
                        }}
                        style={{
                          background: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.5rem',
                          padding: '1rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                          e.currentTarget.style.borderColor = '#3b82f6';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.boxShadow = 'none';
                          e.currentTarget.style.borderColor = '#e5e7eb';
                        }}
                      >
                        {/* Título y Estado */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '0.5rem'
                        }}>
                          <h4 style={{
                            margin: 0,
                            fontSize: '1rem',
                            fontWeight: '600',
                            color: '#111827'
                          }}>
                            {tarea.titulo}
                          </h4>
                          {tarea.estado_kanban_data && (
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              background: tarea.estado_kanban_data.color || '#f3f4f6',
                              color: '#fff'
                            }}>
                              {tarea.estado_kanban_data.nombre}
                            </span>
                          )}
                        </div>

                        {/* Descripción */}
                        {tarea.descripcion && (
                          <p style={{
                            margin: '0.5rem 0',
                            fontSize: '0.875rem',
                            color: '#6b7280',
                            lineHeight: '1.5'
                          }}>
                            {tarea.descripcion}
                          </p>
                        )}

                        {/* Info: Usuario/Equipo, Fechas, Tiempo */}
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '0.75rem',
                          marginTop: '0.75rem',
                          fontSize: '0.75rem',
                          color: '#6b7280'
                        }}>
                          {/* Usuario asignado */}
                          {tarea.usuario_asignado_nombre && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              padding: '0.25rem 0.5rem',
                              background: '#f0f9ff',
                              borderRadius: '6px'
                            }}>
                              <span>👤</span>
                              <span style={{ fontWeight: '500' }}>{tarea.usuario_asignado_nombre}</span>
                            </div>
                          )}

                          {/* Equipo asignado (si no hay usuario asignado) */}
                          {!tarea.usuario_asignado_nombre && tarea.equipo_asignado_nombre && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              padding: '0.25rem 0.5rem',
                              background: tarea.equipo_asignado_color || '#f3f4f6',
                              borderRadius: '6px',
                              color: '#fff'
                            }}>
                              <span>👥</span>
                              <span style={{ fontWeight: '500' }}>{tarea.equipo_asignado_nombre}</span>
                            </div>
                          )}

                          {/* Fecha de inicio */}
                          {tarea.fecha_inicio && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              padding: '0.25rem 0.5rem',
                              background: '#f0fdf4',
                              borderRadius: '6px'
                            }}>
                              <span>🚀</span>
                              <span style={{ fontWeight: '500' }}>
                                Inicio: {(() => {
                                  const [year, month, day] = tarea.fecha_inicio.split('-').map(Number);
                                  const date = new Date(year, month - 1, day);
                                  return date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
                                })()}
                              </span>
                            </div>
                          )}

                          {/* Fecha de término */}
                          {tarea.fecha_termino && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              padding: '0.25rem 0.5rem',
                              background: '#fef2f2',
                              borderRadius: '6px'
                            }}>
                              <span>🏁</span>
                              <span style={{ fontWeight: '500' }}>
                                Fin: {(() => {
                                  const [year, month, day] = tarea.fecha_termino.split('-').map(Number);
                                  const date = new Date(year, month - 1, day);
                                  return date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
                                })()}
                              </span>
                            </div>
                          )}

                          {/* Badge de tiempo registrado */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            marginLeft: 'auto',
                            padding: '0.25rem 0.75rem',
                            background: parseFloat(tarea.total_horas) > 0 ? '#dbeafe' : '#f3f4f6',
                            color: parseFloat(tarea.total_horas) > 0 ? '#1e40af' : '#6b7280',
                            borderRadius: '12px',
                            fontWeight: '600',
                            fontSize: '0.8rem'
                          }}>
                            <i className="fas fa-clock"></i>
                            <span>{parseFloat(tarea.total_horas || 0).toFixed(2)}h</span>
                          </div>
                        </div>

                        {/* Botones de acción */}
                        <div style={{
                          marginTop: '0.75rem',
                          paddingTop: '0.75rem',
                          borderTop: '1px solid #e5e7eb',
                          display: 'flex',
                          gap: '0.5rem',
                          flexWrap: 'wrap'
                        }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTareaForTime(tarea);
                              setShowManualTimeForm(true);
                              cargarRecursosDeTarea(tarea);
                            }}
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'linear-gradient(135deg, #102826, #69B32D)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              transform: 'translateY(0px)',
                              boxShadow: 'none',
                              flex: '1 1 auto'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.transform = 'translateY(0px)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            <i className="fas fa-clock"></i> Agregar Tiempo
                          </button>

                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              // Cargar detalles de la tarea antes de abrir el modal
                              try {
                                const tareaDetallada = await eventosGanttService.tareas.retrieve(tarea.id);
                                setTareaSeleccionadaParaSolicitud(tareaDetallada.data);
                                setShowSolicitudRecursosModal(true);
                              } catch (error) {
                                console.error('Error loading tarea details:', error);
                                alert('Error al cargar los detalles de la tarea');
                              }
                            }}
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'linear-gradient(135deg, #0891b2, #06b6d4)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              transform: 'translateY(0px)',
                              boxShadow: 'none',
                              flex: '1 1 auto'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(8, 145, 178, 0.4)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.transform = 'translateY(0px)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            📦 Solicitar Recursos
                          </button>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTareaEvento(tarea.id);
                            }}
                            style={{
                              padding: '0.5rem 1rem',
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.background = '#dc2626';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.background = '#ef4444';
                            }}
                          >
                            🗑️ Eliminar
                          </button>
                        </div>

                        {/* Formulario de entrada manual de tiempo - dentro del mapa */}
                        {showManualTimeForm && selectedTareaForTime && selectedTareaForTime.id === tarea.id && (
                          <div style={{
                            background: '#f0f9ff',
                            border: '2px solid #3b82f6',
                            borderRadius: '8px',
                            padding: '1.5rem',
                            marginTop: '1rem'
                          }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '1rem'
                            }}>
                              <h4 style={{
                                margin: 0,
                                color: '#1e40af',
                                fontSize: '1rem',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}>
                                <i className="fas fa-clock"></i> Agregar Tiempo Manual
                              </h4>
                              <button
                                onClick={() => {
                                  setShowManualTimeForm(false);
                                  setSelectedTareaForTime(null);
                                  setRecursosDisponiblesTarea([]);
                                  setManualTimeForm({
                                    fecha: new Date().toISOString().split('T')[0],
                                    minutos: '',
                                    notas: '',
                                    presupuesto_costo_recursos: []
                                  });
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  fontSize: '1.5rem',
                                  cursor: 'pointer',
                                  color: '#6b7280',
                                  padding: '0',
                                  lineHeight: '1'
                                }}
                              >
                                ×
                              </button>
                            </div>

                            <form onSubmit={handleAddManualTime}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                <div>
                                  <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    color: '#374151',
                                    fontSize: '0.875rem',
                                    fontWeight: '600'
                                  }}>
                                    Fecha
                                  </label>
                                  <input
                                    type="date"
                                    value={manualTimeForm.fecha}
                                    onChange={(e) => setManualTimeForm({ ...manualTimeForm, fecha: e.target.value })}
                                    required
                                    max={new Date().toISOString().split('T')[0]}
                                    style={{
                                      width: '100%',
                                      padding: '0.5rem',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '6px',
                                      fontSize: '0.875rem',
                                      boxSizing: 'border-box'
                                    }}
                                  />
                                </div>

                                <div>
                                  <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    color: '#374151',
                                    fontSize: '0.875rem',
                                    fontWeight: '600'
                                  }}>
                                    Tiempo (minutos)
                                  </label>
                                  <input
                                    type="number"
                                    min="1"
                                    value={manualTimeForm.minutos}
                                    onChange={(e) => setManualTimeForm({ ...manualTimeForm, minutos: e.target.value })}
                                    required
                                    placeholder="ej: 30"
                                    style={{
                                      width: '100%',
                                      padding: '0.5rem',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '6px',
                                      fontSize: '0.875rem',
                                      boxSizing: 'border-box'
                                    }}
                                  />
                                </div>
                              </div>

                              <div style={{ marginBottom: '1rem' }}>
                                <label style={{
                                  display: 'block',
                                  marginBottom: '0.5rem',
                                  color: '#374151',
                                  fontSize: '0.875rem',
                                  fontWeight: '600'
                                }}>
                                  Notas (opcional)
                                </label>
                                <textarea
                                  value={manualTimeForm.notas}
                                  onChange={(e) => setManualTimeForm({ ...manualTimeForm, notas: e.target.value })}
                                  placeholder="Describe en qué trabajaste..."
                                  rows="3"
                                  style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    fontSize: '0.875rem',
                                    resize: 'vertical',
                                    boxSizing: 'border-box'
                                  }}
                                />
                              </div>

                              {/* Recursos del Presupuesto (multiselect) */}
                              {recursosDisponiblesTarea.length > 0 && (
                                <div style={{ marginBottom: '1rem' }}>
                                  <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    color: '#374151',
                                    fontSize: '0.875rem',
                                    fontWeight: '600'
                                  }}>
                                    Recursos Utilizados (opcional)
                                    <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '8px', fontWeight: '400' }}>
                                      Mantén presionado Ctrl (Cmd en Mac) para seleccionar múltiples
                                    </span>
                                  </label>
                                  <select
                                    multiple
                                    value={manualTimeForm.presupuesto_costo_recursos}
                                    onChange={(e) => {
                                      // Obtener todos los valores seleccionados
                                      const selectedOptions = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                                      setManualTimeForm(prev => ({
                                        ...prev,
                                        presupuesto_costo_recursos: selectedOptions
                                      }));
                                    }}
                                    style={{
                                      width: '100%',
                                      padding: '0.5rem',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '6px',
                                      fontSize: '0.875rem',
                                      minHeight: '100px',
                                      boxSizing: 'border-box'
                                    }}
                                  >
                                    {recursosDisponiblesTarea.map(recurso => (
                                      <option key={recurso.id} value={recurso.id}>
                                        {recurso.nombre} - ${recurso.precio_unitario_costo?.toLocaleString('es-CL')}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}

                              <div style={{
                                display: 'flex',
                                gap: '0.75rem',
                                justifyContent: 'flex-end'
                              }}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setShowManualTimeForm(false);
                                    setSelectedTareaForTime(null);
                                    setRecursosDisponiblesTarea([]);
                                    setManualTimeForm({
                                      fecha: new Date().toISOString().split('T')[0],
                                      minutos: '',
                                      notas: '',
                                      presupuesto_costo_recursos: []
                                    });
                                  }}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    background: '#e5e7eb',
                                    color: '#374151',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  Cancelar
                                </button>
                                <button
                                  type="submit"
                                  style={{
                                    padding: '0.5rem 1rem',
                                    background: 'linear-gradient(135deg, #102826, #69B32D)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  Guardar Tiempo
                                </button>
                              </div>
                            </form>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

              </div>
            )}

            {activeTab === 'gantt' && (
              <GanttChart eventoId={eventoId} />
            )}

            {activeTab === 'comments' && (
              <CommentsSection postId={eventoId} users={users} />
            )}
          </div>
      </div>

      {/* Modal para crear/editar tarea */}
      <TareaEventoModal
        isOpen={showTareaModal}
        onClose={() => setShowTareaModal(false)}
        eventoId={eventoId}
        actividadId={actividadSeleccionadaParaTarea}
        onSave={() => {
          setShowTareaModal(false);
          loadTareasDelEvento();
        }}
      />

      {/* Modal para ver/agregar tiempo de tarea */}
      <TareaEventoTimeModal
        isOpen={showTimeModal}
        onClose={() => {
          setShowTimeModal(false);
          setSelectedTareaForTime(null);
        }}
        tarea={selectedTareaForTime}
        onTimeLogAdded={() => {
          loadTareasDelEvento();
        }}
      />

      {/* Modal para solicitar recursos */}
      {showSolicitudRecursosModal && tareaSeleccionadaParaSolicitud && (
        <SolicitudRecursosModal
          tarea={tareaSeleccionadaParaSolicitud}
          onClose={() => {
            setShowSolicitudRecursosModal(false);
            setTareaSeleccionadaParaSolicitud(null);
          }}
          onCreated={() => {
            loadTareasDelEvento();
          }}
        />
      )}
    </div>
  );
};

export default EventoDetailModal;
